import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { humanize, timeOnly, todayIso } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { DataTable, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Select, StatusBadge } from '@/components/ui/primitives';

interface OtCase {
  id: string;
  status: string;
  procedureName: string;
  anesthesiaType: string;
  scheduledStart: string;
  estimatedMinutes: number;
  actualStart: string | null;
  actualEnd: string | null;
  patient: { id: string; mrn: string; fullName: string; sex: string };
  theatre: { id: string; code: string; name: string };
  admission: { id: string; number: string } | null;
}

export interface OtCaseDetail extends OtCase {
  operativeNotes: string | null;
  anesthesiaRecord: string | null;
  postOpNotes: string | null;
  spongeCountCorrect: boolean | null;
  instrumentCountCorrect: boolean | null;
  whoChecklistItems: { key: string; done: boolean }[];
  whoChecklistComplete: boolean;
  patient: OtCase['patient'] & { allergies: { id: string; substance: string }[] };
}

export const STATUS_FLOW = ['scheduled', 'pre_op', 'in_theatre', 'recovery', 'completed'];

const THEATRE_LIST_LIMIT = 200;

/** The theatre list for a day (SRS 6.7). */
export function TheatrePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(todayIso());
  const [theatreId, setTheatreId] = useState('');

  const theatres = useQuery({
    queryKey: ['theatres'],
    queryFn: () => get<{ id: string; code: string; name: string }[]>('/ot/theatres'),
  });

  const cases = useQuery({
    queryKey: ['ot-cases', date, theatreId],
    queryFn: () =>
      get<{ data: OtCase[]; nextCursor: string | null }>(
        `/ot/cases${qs({ date, theatreId: theatreId || undefined, limit: THEATRE_LIST_LIMIT })}`,
      ),
    refetchInterval: visibleInterval(60_000),
  });

  const list = cases.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Operation theatre"
        description="The day's list, with the safety checklist that gates it"
        actions={can('ot.manage') && <Button onClick={() => navigate(`/ot/cases/new?date=${date}`)}>Schedule case</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Cases listed" value={list.length} />
        <StatTile label="In theatre" value={list.filter((c) => c.status === 'in_theatre').length} tone="warning" />
        <StatTile label="Completed" value={list.filter((c) => c.status === 'completed').length} tone="success" />
        <StatTile
          label="Theatre minutes booked"
          value={list.reduce((sum, c) => sum + c.estimatedMinutes, 0)}
        />
      </div>

      {cases.data?.nextCursor && (
        <Alert variant="warning" className="mb-4">
          Showing the first {THEATRE_LIST_LIMIT} cases of this day. Pick a single theatre to see the rest.
        </Alert>
      )}

      <SearchFilterBar
        filters={
          <>
            <FilterField label="Date">
              <DatePicker value={date} onChange={setDate} className="w-40" />
            </FilterField>
            <FilterField label="Theatre">
              <Select value={theatreId} onChange={(e) => setTheatreId(e.target.value)} className="w-44">
                <option value="">All theatres</option>
                {theatres.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        onReset={
          theatreId || date !== todayIso()
            ? () => {
                setTheatreId('');
                setDate(todayIso());
              }
            : undefined
        }
      />

      <DataTable
        rows={list}
        loading={cases.isLoading} refreshing={cases.isFetching}
        error={cases.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/ot/cases/${row.id}`)}
        empty="No cases listed for this day."
        columns={[
          {
            header: 'Slot',
            width: '130px',
            sort: (row) => row.scheduledStart,
            cell: (row) => (
              <div>
                <p className="tabular font-medium">{timeOnly(row.scheduledStart)}</p>
                <p className="text-xs text-muted-foreground">{row.estimatedMinutes} min</p>
              </div>
            ),
          },
          { header: 'Theatre', sort: (row) => row.theatre.name, cell: (row) => row.theatre.name },
          {
            header: 'Patient',
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn}
                  {row.admission ? ` · ${row.admission.number}` : ''}
                </p>
              </div>
            ),
          },
          { header: 'Procedure', sort: (row) => row.procedureName, cell: (row) => row.procedureName },
          { header: 'Anaesthesia', sort: (row) => row.anesthesiaType, cell: (row) => <Badge variant="secondary">{humanize(row.anesthesiaType, 'anesthesia_type')}</Badge> },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
      />
    </>
  );
}
